import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Sparkles, ShieldCheck, Quote, Star } from "lucide-react";
import { api } from "@/lib/api";

const HERO_IMG = "/images/hero.png";

const features = [
  { icon: Leaf, title: "Classically Trained", body: "Vaidyas from Jamnagar, Coimbatore and Kerala — schooled in the unbroken tradition." },
  { icon: Sparkles, title: "Authentic Panchakarma", body: "Multi-week protocols administered by certified therapists with classical oils & herbs." },
  { icon: ShieldCheck, title: "Pure Herbal Medicines", body: "Hand-ground in small batches. Sourced from organic farms across India." },
];

const Home = () => {
  const [products, setProducts] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 4))).catch(() => {});
    api.get("/treatments").then((r) => setTreatments(r.data.slice(0, 6))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[88vh]">
          <div className="lg:col-span-6 flex items-center px-6 md:px-12 lg:px-20 py-20 lg:py-0 order-2 lg:order-1">
            <div className="max-w-xl fade-up">
              <p className="overline mb-6">Est. — Classical Ayurveda</p>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] font-light mb-8">
                Healing the way it has been done for <em className="font-display italic" style={{ color: "var(--ayu-secondary)" }}>five thousand years.</em>
              </h1>
              <p className="text-base leading-relaxed mb-10 max-w-md" style={{ color: "var(--ayu-body)" }}>
                A classical Ayurveda clinic, herbal apothecary, and Panchakarma sanctuary —
                rooted in the texts of Charaka, calibrated for the modern life.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/services" data-testid="hero-cta-services" className="btn-primary">
                  Explore Panchakarma <ArrowRight size={16} />
                </Link>
                <Link to="/shop/medicines" data-testid="hero-cta-shop" className="btn-secondary">
                  Visit the Apothecary
                </Link>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 relative order-1 lg:order-2 min-h-[50vh] lg:min-h-full">
            <img
              src={HERO_IMG}
              alt="Ayurvedic massage therapy"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(250,248,245,0.4) 0%, transparent 30%)" }} />
            <div className="hidden lg:block absolute bottom-12 left-12 p-8 max-w-xs backdrop-blur-md" style={{ background: "rgba(250,248,245,0.85)" }}>
              <p className="overline mb-3">A note from our Vaidya</p>
              <p className="font-display text-xl leading-snug" style={{ color: "var(--ayu-heading)" }}>
                "Ayurveda does not chase disease. It restores the rhythm of the one who suffers."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-32" style={{ background: "var(--ayu-surface)" }}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {features.map((f, i) => (
            <div key={i} data-testid={`pillar-${i}`} className="flex flex-col gap-4">
              <f.icon size={32} strokeWidth={1.2} style={{ color: "var(--ayu-secondary)" }} />
              <h3 className="text-2xl">{f.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--ayu-body)" }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TREATMENTS */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-xl">
            <p className="overline mb-4">Panchakarma</p>
            <h2 className="text-4xl sm:text-5xl leading-tight">The Five Classical Cleansing Actions</h2>
          </div>
          <Link to="/services" data-testid="view-all-treatments" className="text-sm underline underline-offset-4" style={{ color: "var(--ayu-secondary)" }}>
            View all treatments →
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {treatments.map((t) => (
            <Link
              key={t.id}
              to={`/services#${t.slug}`}
              data-testid={`treatment-card-${t.slug}`}
              className="group block relative overflow-hidden"
              style={{ background: "var(--ayu-surface)" }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img src={t.image} alt={t.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="p-6">
                <p className="overline mb-2">{t.sanskrit_name}</p>
                <h3 className="text-2xl mb-2">{t.name}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--ayu-body)" }}>{t.short_description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* APOTHECARY */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-32" style={{ background: "var(--ayu-surface)" }}>
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-xl">
            <p className="overline mb-4">The Apothecary</p>
            <h2 className="text-4xl sm:text-5xl leading-tight">Hand-ground. Small batch. Slow medicine.</h2>
          </div>
          <Link to="/shop/medicines" data-testid="view-all-products" className="text-sm underline underline-offset-4" style={{ color: "var(--ayu-secondary)" }}>
            Browse all medicines →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => (
            <Link
              key={p.id}
              to={`/product/${p.slug}`}
              data-testid={`featured-product-${p.slug}`}
              className="group block bg-[var(--ayu-bg)] p-3"
            >
              <div className="aspect-square overflow-hidden mb-4">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="px-2 pb-3">
                <h4 className="font-display text-lg leading-snug">{p.name}</h4>
                <p className="text-xs mt-1 opacity-60">{p.sub_category}</p>
                <p className="mt-3 text-sm" style={{ color: "var(--ayu-secondary)" }}>₹{p.price}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 relative">
          <img src="https://images.pexels.com/photos/4113884/pexels-photo-4113884.jpeg?auto=compress&cs=tinysrgb&w=1000" alt="Fresh herbs" className="w-full h-[60vh] object-cover" loading="lazy" />
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <p className="overline mb-4">Our Philosophy</p>
          <h2 className="text-4xl sm:text-5xl leading-tight mb-8">
            We do not treat the symptom. We tend to the person who carries it.
          </h2>
          <p className="leading-relaxed mb-6" style={{ color: "var(--ayu-body)" }}>
            Every consultation begins not with a question of "what is wrong", but of "who you are".
            Your Prakriti — your innate constitution — guides every herb we prescribe, every oil we
            select, every meal we suggest.
          </p>
          <p className="leading-relaxed mb-10" style={{ color: "var(--ayu-body)" }}>
            From there, we work patiently. We use the herbs grown nearest to where you live, the
            therapies most suited to the season, and the wisdom most relevant to your stage of life.
          </p>
          <Link to="/about" data-testid="about-link" className="btn-secondary">Read our story</Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-32" style={{ background: "var(--ayu-heading)", color: "var(--ayu-bg)" }}>
        <div className="max-w-2xl mb-16">
          <p className="overline mb-4" style={{ color: "var(--ayu-accent)" }}>Patient Voices</p>
          <h2 className="text-4xl sm:text-5xl leading-tight" style={{ color: "var(--ayu-bg)" }}>
            Trusted by seekers across India and the world.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {testimonials.map((t) => (
            <div key={t.id} data-testid={`testimonial-${t.id}`}>
              <Quote size={28} strokeWidth={1} className="mb-6" style={{ color: "var(--ayu-secondary)" }} />
              <p className="font-display text-2xl leading-snug mb-6" style={{ color: "var(--ayu-bg)" }}>"{t.quote}"</p>
              <div className="flex gap-1 mb-3">
                {[...Array(t.rating)].map((_, i) => <Star key={i} size={14} fill="currentColor" style={{ color: "var(--ayu-accent)" }} />)}
              </div>
              <p className="text-sm opacity-80">{t.name} · {t.location}</p>
              <p className="text-xs opacity-60 mt-1">{t.treatment}</p>
            </div>
          ))}
        </div>
        <div className="mt-16">
          <Link to="/testimonials" data-testid="view-all-testimonials" className="text-sm underline underline-offset-4" style={{ color: "var(--ayu-accent)" }}>
            Read more stories →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 text-center">
        <p className="overline mb-6">Begin your journey</p>
        <h2 className="text-4xl sm:text-6xl leading-tight max-w-3xl mx-auto mb-10">
          Your first consultation costs nothing but an honest conversation.
        </h2>
        <Link to="/contact" data-testid="cta-book-consultation" className="btn-accent">
          Book a consultation <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
};

export default Home;
